import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import './SimpleBarChart.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const SimpleBarChart = ({ 
  data = [], 
  activeIndex = -1, // 활성 인덱스 (없으면 -1)
  className = '' 
}) => {
  const chartRef = useRef(null);
  const [colors, setColors] = useState({
    primary: '#2563eb',
    gray200: '#e5e7eb',
    gray300: '#d1d5db',
    gray600: '#4b5563'
  });
  const [hoveredValue, setHoveredValue] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(-1);
  const [barPosition, setBarPosition] = useState({ x: 0, y: 0 });

  // CSS 변수에서 색상 값 가져오기
  useEffect(() => {
    const getCSSVariable = (variable) => {
      return getComputedStyle(document.documentElement)
        .getPropertyValue(variable)
        .trim();
    };

    setColors({
      primary: getCSSVariable('--primary-600') || '#2563eb',
      gray200: getCSSVariable('--gray-200') || '#e5e7eb',
      gray300: getCSSVariable('--gray-300') || '#d1d5db',
      gray600: getCSSVariable('--gray-600') || '#4b5563'
    });
  }, []);

  // 호버 이벤트 최적화
  const handleHover = useCallback((event, activeElements, chart) => {
    if (activeElements.length > 0) {
      const dataIndex = activeElements[0].index;
      const value = data[dataIndex]?.value;
      
      // 이전 값과 같으면 업데이트하지 않음
      if (hoveredValue === value && hoveredIndex === dataIndex) {
        return;
      }
      
      setHoveredValue(`${data[dataIndex]?.label}시 ${value}원`);
      setHoveredIndex(dataIndex);
      
      // 바의 정확한 위치 계산
      if (chart && chart.chartArea) {
        const xScale = chart.scales.x;
        const yScale = chart.scales.y;
        const barX = xScale.getPixelForValue(dataIndex);
        const barTop = yScale.getPixelForValue(value)-10;
        
        setBarPosition({
          x: barX,
          y: barTop - 20
        });
      }
    } else {
      // 호버 해제 시에만 상태 업데이트
      if (hoveredValue !== null) {
        setHoveredValue(null);
        setHoveredIndex(-1);
      }
    }
  }, [hoveredValue, hoveredIndex, data]);

  // 가장 높은 값의 인덱스 찾기 (메모이제이션)
  const { maxValue, maxValueIndex, targetIndex } = useMemo(() => {
    const maxVal = Math.max(...data.map(item => item.value));
    const maxIdx = data.findIndex(item => item.value === maxVal);
    const targetIdx = activeIndex !== -1 ? activeIndex : maxIdx;
    
    return { maxValue: maxVal, maxValueIndex: maxIdx, targetIndex: targetIdx };
  }, [data, activeIndex]);

  // ChartJS 데이터 형식으로 변환 (메모이제이션)
  const chartData = useMemo(() => ({
    labels: data.map(item => item.label),
    datasets: [{
      data: data.map(item => item.value),
      backgroundColor: data.map((_, index) => 
        index === targetIndex ? colors.primary : colors.gray200
      ),
      borderColor: data.map((_, index) => 
        index === targetIndex ? colors.primary : colors.gray300
      ),
      borderWidth: 0,
      borderRadius: 8,
      borderSkipped: false,
    }]
  }), [data, targetIndex, colors]);

  // 차트 옵션 (메모이제이션)
  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: false
      }
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: false
        },
        border: {
          display: false
        },
        ticks: {
          display: false
        }
      },
      y: {
        display: false,
        grid: {
          display: false
        }
      }
    },
    interaction: {
      intersect: false
    },
    onHover: handleHover
  }), [colors, handleHover]);

  // 차트 영역을 벗어났을 때 상태 초기화
  const handleMouseLeave = useCallback(() => {
    setHoveredValue(null);
    setHoveredIndex(-1);
  }, []);

  return (
    <div 
      className={`simple-bar-chart ${className}`}
      onMouseLeave={handleMouseLeave}
    >
      <Bar 
        ref={chartRef}
        data={chartData} 
        options={options} 
      />
      {hoveredValue !== null && (
        <div 
          className="bar-value-display"
          style={{
            position: 'absolute',
            left: `${barPosition.x}px`,
            top: `${barPosition.y}px`,
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            padding: '4px 8px',
            borderRadius: '100vh',
            fontSize: '12px',
            fontWeight: 'bold',
            pointerEvents: 'none',
            zIndex: 10,
            whiteSpace: 'nowrap'
          }}
        >
          {hoveredValue}
        </div>
      )}
    </div>
  );
};

export default SimpleBarChart;
